import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Save, Eye, Power, AlertCircle, CheckCircle2 } from "lucide-react";

interface Plantilla {
  id: string;
  codigo: string;
  nombre: string;
  descripcion: string | null;
  asunto: string;
  cuerpo: string;
  variablesDoc: string[];
  activa: boolean;
}

type Editor = { mode: "new" } | { mode: "edit"; id: string } | null;

const empty = {
  codigo: "",
  nombre: "",
  descripcion: "",
  asunto: "",
  cuerpo: "",
  variablesDocCsv: "",
  activa: true,
};

export function PlantillasPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["plantillas"],
    queryFn: async () => (await api.get<Plantilla[]>("/api/admin/notificaciones/plantillas")).data,
  });
  const [editor, setEditor] = useState<Editor>(null);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Plantillas de notificación</h1>
          <p className="text-sm text-fg-muted">Edición Mustache de asunto y cuerpo con preview vivo.</p>
        </div>
        <button
          className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover"
          onClick={() => setEditor({ mode: "new" })}
        >
          <Plus className="h-4 w-4" /> Nueva plantilla
        </button>
      </header>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="rounded-lg border bg-surface shadow-card">
            {list.isLoading && <p className="p-4 text-sm text-fg-subtle">Cargando…</p>}
            {list.error && (
              <p className="p-4 text-sm text-estado-err">Error cargando plantillas.</p>
            )}
            <ul className="divide-y">
              {list.data?.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => setEditor({ mode: "edit", id: p.id })}
                    className={`flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition hover:bg-surface-2 ${
                      editor && editor.mode === "edit" && editor.id === p.id ? "bg-estado-info-bg" : ""
                    }`}
                  >
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${p.activa ? "bg-action-ok" : "bg-fg-faint"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-xs text-fg-subtle">{p.codigo}</div>
                      <div className="truncate font-medium text-fg">{p.nombre}</div>
                      {p.descripcion && (
                        <div className="truncate text-xs text-fg-subtle">{p.descripcion}</div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
              {list.data?.length === 0 && (
                <li className="p-4 text-sm text-fg-subtle">No hay plantillas. Crea la primera.</li>
              )}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          {editor ? (
            <PlantillaEditor
              key={editor.mode === "new" ? "new" : editor.id}
              editor={editor}
              plantilla={editor.mode === "edit" ? list.data?.find((p) => p.id === editor.id) : undefined}
              onClose={() => setEditor(null)}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ["plantillas"] });
              }}
            />
          ) : (
            <div className="rounded-lg border bg-surface p-6 text-center text-sm text-fg-subtle shadow-card">
              Selecciona una plantilla o crea una nueva.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EditorProps {
  editor: NonNullable<Editor>;
  plantilla: Plantilla | undefined;
  onClose: () => void;
  onSaved: () => void;
}

function PlantillaEditor({ editor, plantilla, onClose, onSaved }: EditorProps) {
  const isNew = editor.mode === "new";
  const [form, setForm] = useState(() =>
    plantilla
      ? {
          codigo: plantilla.codigo,
          nombre: plantilla.nombre,
          descripcion: plantilla.descripcion ?? "",
          asunto: plantilla.asunto,
          cuerpo: plantilla.cuerpo,
          variablesDocCsv: plantilla.variablesDoc.join(", "),
          activa: plantilla.activa,
        }
      : empty,
  );
  const [tab, setTab] = useState<"editar" | "preview">("editar");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  // Cuando cambia la plantilla seleccionada, sincroniza el form.
  useEffect(() => {
    if (plantilla) {
      setForm({
        codigo: plantilla.codigo,
        nombre: plantilla.nombre,
        descripcion: plantilla.descripcion ?? "",
        asunto: plantilla.asunto,
        cuerpo: plantilla.cuerpo,
        variablesDocCsv: plantilla.variablesDoc.join(", "),
        activa: plantilla.activa,
      });
    }
  }, [plantilla]);

  const guardar = useMutation({
    mutationFn: async () => {
      setError(null);
      const variablesDoc = form.variablesDocCsv
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      if (isNew) {
        const body = {
          codigo: form.codigo,
          nombre: form.nombre,
          descripcion: form.descripcion || null,
          asunto: form.asunto,
          cuerpo: form.cuerpo,
          variablesDoc,
        };
        await api.post("/api/admin/notificaciones/plantillas", body);
      } else {
        const body = {
          nombre: form.nombre,
          descripcion: form.descripcion || null,
          asunto: form.asunto,
          cuerpo: form.cuerpo,
          variablesDoc,
          activa: form.activa,
        };
        await api.put(`/api/admin/notificaciones/plantillas/${(editor as { mode: "edit"; id: string }).id}`, body);
      }
    },
    onSuccess: () => {
      onSaved();
      if (isNew) onClose();
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail ?? "Error al guardar.");
    },
  });

  const desactivar = useMutation({
    mutationFn: async () => {
      const id = (editor as { mode: "edit"; id: string }).id;
      await api.delete(`/api/admin/notificaciones/plantillas/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["plantillas"] });
      onSaved();
      onClose();
    },
  });

  return (
    <div className="rounded-lg border bg-surface shadow-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h2 className="font-semibold text-fg">
            {isNew ? "Nueva plantilla" : plantilla?.nombre ?? "—"}
          </h2>
          {!isNew && plantilla && (
            <p className="font-mono text-xs text-fg-subtle">{plantilla.codigo}</p>
          )}
        </div>
        <div className="flex gap-1 rounded border bg-surface-2 p-0.5 text-sm">
          {(["editar", "preview"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`cursor-pointer rounded px-3 py-1 transition ${tab === t ? "bg-surface shadow" : "text-fg-muted"}`}
            >
              {t === "editar" ? "Editar" : "Preview"}
            </button>
          ))}
        </div>
      </div>

      {tab === "editar" ? (
        <EditTab
          form={form}
          isNew={isNew}
          activa={plantilla?.activa ?? true}
          onChange={setForm}
        />
      ) : (
        <PreviewTab asunto={form.asunto} cuerpo={form.cuerpo} />
      )}

      {error && (
        <div className="mx-4 mb-3 flex items-start gap-2 rounded-md bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <div>{error}</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 border-t bg-surface-2 px-4 py-3">
        <button onClick={onClose} className="cursor-pointer rounded px-3 py-1.5 text-sm text-fg-muted transition hover:bg-surface-2">
          Cancelar
        </button>
        {!isNew && plantilla?.activa && (
          <button
            onClick={() => {
              if (confirm("¿Desactivar esta plantilla? Quedará inservible para nuevos envíos.")) {
                desactivar.mutate();
              }
            }}
            className="inline-flex cursor-pointer items-center gap-1 rounded border border-estado-err/25 px-3 py-1.5 text-sm text-estado-err transition hover:bg-estado-err-bg"
          >
            <Power className="h-4 w-4" /> Desactivar
          </button>
        )}
        <button
          onClick={() => guardar.mutate()}
          disabled={guardar.isPending}
          className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </div>
  );
}

interface FormState {
  codigo: string;
  nombre: string;
  descripcion: string;
  asunto: string;
  cuerpo: string;
  variablesDocCsv: string;
  activa: boolean;
}

function EditTab({
  form,
  isNew,
  activa,
  onChange,
}: {
  form: FormState;
  isNew: boolean;
  activa: boolean;
  onChange: (f: FormState) => void;
}) {
  return (
    <div className="space-y-3 p-4 text-sm">
      <Field label="Código" hint="UPPERCASE_CON.PUNTOS (inmutable si ya existe)">
        <input
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg font-mono disabled:bg-surface-2"
          value={form.codigo}
          disabled={!isNew}
          onChange={(e) => onChange({ ...form, codigo: e.target.value.toUpperCase() })}
        />
      </Field>
      <Field label="Nombre">
        <input
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg"
          value={form.nombre}
          onChange={(e) => onChange({ ...form, nombre: e.target.value })}
        />
      </Field>
      <Field label="Descripción">
        <input
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg"
          value={form.descripcion}
          onChange={(e) => onChange({ ...form, descripcion: e.target.value })}
        />
      </Field>
      <Field label="Asunto (Mustache)" hint="ej. SICEN — Tu certificado {{folio}} ha sido emitido">
        <input
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg font-mono"
          value={form.asunto}
          onChange={(e) => onChange({ ...form, asunto: e.target.value })}
        />
      </Field>
      <Field label="Cuerpo (Mustache)">
        <textarea
          className="h-48 w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg font-mono text-xs"
          value={form.cuerpo}
          onChange={(e) => onChange({ ...form, cuerpo: e.target.value })}
        />
      </Field>
      <Field label="Variables documentadas (CSV)" hint="Solo informativo; lista las claves esperadas">
        <input
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg font-mono text-xs"
          value={form.variablesDocCsv}
          placeholder="folio, titular_razon_social, producto, …"
          onChange={(e) => onChange({ ...form, variablesDocCsv: e.target.value })}
        />
      </Field>
      {!isNew && (
        <Field label="Estado">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.activa}
              onChange={(e) => onChange({ ...form, activa: e.target.checked })}
            />
            <span className="text-sm">
              {activa ? "Activa" : "Inactiva"} {form.activa !== activa && "(cambio pendiente)"}
            </span>
          </label>
        </Field>
      )}
    </div>
  );
}

function PreviewTab({ asunto, cuerpo }: { asunto: string; cuerpo: string }) {
  const [modeloJson, setModeloJson] = useState(
    JSON.stringify(
      {
        folio: "NPMX-T-2026-0001",
        titular_razon_social: "Empresa de Ejemplo S.A. de C.V.",
        producto: "Auricular bluetooth RP-HF410B",
        norma: "NOM-208-SCFI-2016",
        estado_codigo: "EVALUACION_TECNICA",
        sla_dias: 7,
        dias_atraso: 2,
        fecha_vencimiento: "2026-05-14",
      },
      null,
      2,
    ),
  );
  const [resultado, setResultado] = useState<{ asunto: string; cuerpo: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const correr = useMutation({
    mutationFn: async () => {
      setError(null);
      let modelo: Record<string, unknown> = {};
      try {
        modelo = JSON.parse(modeloJson);
      } catch {
        throw new Error("Modelo JSON inválido.");
      }
      const r = await api.post<{ asuntoRenderizado: string; cuerpoRenderizado: string }>(
        "/api/admin/notificaciones/plantillas/preview",
        { asunto, cuerpo, modelo },
      );
      return r.data;
    },
    onSuccess: (d) => setResultado({ asunto: d.asuntoRenderizado, cuerpo: d.cuerpoRenderizado }),
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { detail?: string } }; message?: string };
      setError(ax.response?.data?.detail ?? ax.message ?? "Error.");
    },
  });

  const submitDisabled = useMemo(() => !asunto?.trim() || !cuerpo?.trim(), [asunto, cuerpo]);

  return (
    <div className="space-y-3 p-4 text-sm">
      <Field label="Modelo de prueba (JSON)">
        <textarea
          className="h-40 w-full rounded border border-edge-strong bg-surface px-2 py-1 text-fg font-mono text-xs"
          value={modeloJson}
          onChange={(e) => setModeloJson(e.target.value)}
        />
      </Field>
      <button
        onClick={() => correr.mutate()}
        disabled={correr.isPending || submitDisabled}
        className="inline-flex cursor-pointer items-center gap-1 rounded border border-primary-strong px-3 py-1 text-sm font-medium text-primary-strong transition hover:bg-primary hover:text-white disabled:opacity-50"
      >
        <Eye className="h-4 w-4" /> Renderizar
      </button>

      {error && (
        <div className="flex items-start gap-2 rounded-md bg-estado-warn-bg p-3 text-sm text-estado-warn">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <div>{error}</div>
        </div>
      )}

      {resultado && (
        <div className="space-y-2 rounded border bg-estado-ok-bg p-3">
          <div className="flex items-center gap-2 text-estado-ok">
            <CheckCircle2 className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Resultado</span>
          </div>
          <div>
            <div className="text-xs text-fg-subtle">Asunto</div>
            <div className="font-medium">{resultado.asunto}</div>
          </div>
          <div>
            <div className="text-xs text-fg-subtle">Cuerpo</div>
            <pre className="whitespace-pre-wrap rounded border bg-surface p-2 text-xs">
              {resultado.cuerpo}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium uppercase tracking-wide text-fg-subtle">{label}</label>
      {children}
      {hint && <div className="mt-1 text-xs text-fg-faint">{hint}</div>}
    </div>
  );
}
